import React, { useState, useEffect, useContext, useRef } from "react";
import { VectorMap } from "@react-jvectormap/core";
// import { arMill } from "@react-jvectormap/argentina"; // Commented out - using only Brazil for now
import { brMill } from "@react-jvectormap/brazil";
// import { usMill } from "@react-jvectormap/unitedstates"; // Commented out - using only Brazil for now
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";
import { makeStyles, Tooltip, Select, MenuItem } from "@material-ui/core";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import "./InteractiveMap.css";
import { getAllMapData } from "../../../viewServices/calls/apiCalls";

const interactiveMapStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  mapContainer: {
    width: "100%",
    position: "relative",
  },
  mapContent: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  loadingContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  loadingOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    pointerEvents: "none",
  },
  errorContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#d32f2f",
    fontSize: "1rem",
  },
  resetButton: {
    position: "absolute !important",
    top: "62px !important",
    left: "10px !important",
    width: "24px !important",
    height: "24px !important",
    fontSize: "12px !important",
    fontWeight: "bold !important",
    color: "#333333 !important",
    background: "rgba(240, 240, 240, 0.95) !important",
    border: "1px solid rgba(200, 200, 200, 0.8) !important",
    borderTop: "none !important",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15) !important",
    borderRadius: "0px 0px 4px 4px !important",
    cursor: "pointer !important",
    lineHeight: "1 !important",
    textAlign: "center !important",
    boxSizing: "border-box !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
    padding: "0 !important",
    margin: "0 !important",
    zIndex: "1000 !important",
    transition: "all 0.2s ease !important",
    WebkitUserSelect: "none !important",
    userSelect: "none !important",
    "&:hover": {
      background: "rgba(220, 220, 220, 0.98) !important",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25) !important",
      transform: "translateY(-1px) !important",
    },
  },
  toggleColorsButton: {
    position: "absolute !important",
    bottom: "100px !important",
    left: "0px !important",
    width: "24px !important",
    height: "24px !important",
    fontSize: "12px !important",
    fontWeight: "bold !important",
    color: "#333333 !important",
    background: "rgba(240, 240, 240, 0.95) !important",
    border: "1px solid rgba(200, 200, 200, 0.8) !important",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15) !important",
    borderRadius: "3px !important",
    cursor: "pointer !important",
    lineHeight: "1 !important",
    textAlign: "center !important",
    boxSizing: "border-box !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
    padding: "0 !important",
    margin: "0 !important",
    zIndex: "1000 !important",
    transition: "all 0.2s ease !important",
    WebkitUserSelect: "none !important",
    userSelect: "none !important",
    "& .MuiSvgIcon-root": {
      fontSize: "18px !important",
    },
    "&:hover": {
      background: "rgba(220, 220, 220, 0.98) !important",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25) !important",
      transform: "translateY(-1px) !important",
    },
  },
  vectorMapContainer: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  legendContainer: {
    position: "absolute !important",
    bottom: "15px !important",
    left: "15px !important",
    display: "flex !important",
    alignItems: "center !important",
    zIndex: "1000 !important",
  },
  legendGradient: {
    width: "8px !important",
    height: "80px !important",
    borderRadius: "2px !important",
    marginRight: "8px !important",
    border: "1px solid rgba(200, 200, 200, 0.5) !important",
  },
  legendText: {
    display: "flex !important",
    flexDirection: "column !important",
    alignItems: "flex-start !important",
    justifyContent: "space-between !important",
    height: "80px !important",
    fontSize: "10px !important",
    color: "#333333 !important",
    fontWeight: "500 !important",
    lineHeight: "1.2 !important",
    textAlign: "left !important",
    userSelect: "none !important",
  },
  legendHigh: {
    fontSize: "9px !important",
    color: "#666666 !important",
    fontWeight: "400 !important",
  },
  legendMetric: {
    fontSize: "11px !important",
    fontWeight: "500 !important",
    color: "#333333 !important",
    maxWidth: "60px !important",
    wordBreak: "break-word !important",
  },
  legendDataType: {
    fontSize: "9px !important",
    color: "#666666 !important",
    fontWeight: "500 !important",
  },
  legendLow: {
    fontSize: "9px !important",
    color: "#666666 !important",
    fontWeight: "400 !important",
  },
  legendCenter: {
    display: "flex !important",
    flexDirection: "column !important",
    alignItems: "flex-start !important",
    gap: "1px !important",
  },
  legendDropdown: {
    fontSize: "11px !important",
    minWidth: "70px !important",
    maxWidth: "70px !important",
    "& .MuiSelect-select": {
      padding: "4px 0px 4px 4px !important",
      fontSize: "11px !important",
    },
    "&:before": {
      borderBottomWidth: "1px !important",
      borderBottomColor: grayColor[4] + "!important",
    },
    "&:after": {
      borderBottomColor: clientColor + "!important",
    },
  },
});

const InteractiveMap = ({
  selectedScenario,
  appliedFilters,
  height = "400px",
  dataType = "sellthrough",
  selectedColorMetric = "margin",
  selectedTooltipDetail = "channel",
  selectedLocation = "node2_location",
  selectedVolume = "volume_sellthrough",
  onColorMetricChange,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    interactiveMapStyle(theme, clientColor),
  );
  const classes = useStyles();
  const T_PREFIX = "rgm.cockpit.map";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [allMapData, setAllMapData] = useState({}); // Store all data types
  const [colorScaleInfo, setColorScaleInfo] = useState({});
  const [mapToDisplay, setMapToDisplay] = useState({
    map: brMill,
    strokeWidth: 300,
  });
  const [isShowMap, setIsShowMap] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isColorsInverted, setIsColorsInverted] = useState(false);
  const mapRef = useRef(null);
  const vectorMapRef = useRef(null);

  // Cache map data to avoid refetching on remount
  const cachedMapData = useRef({});

  // Color scheme for the map
  const colorScale = ["#F8696B", "#FBAA77", "#FFEB84", "#B1D580", "#63BE7B"];
  const invertedColorScale = [...colorScale].reverse(); // Inverted color scale

  // Map configurations - using only Brazil for now
  const mapConfigs = {
    // ar: { map: arMill, strokeWidth: 1 }, // Commented out - using only Brazil for now
    br: { map: brMill, strokeWidth: 300 },
    // us: { map: usMill, strokeWidth: 1 }, // Commented out - using only Brazil for now
  };

  // Dynamic color value calculation using statistical distribution
  const calculateDynamicColorValue = (regionData, metric = "margin") => {
    if (
      !regionData ||
      typeof regionData !== "object" ||
      !colorScaleInfo[metric]
    ) {
      return 0;
    }

    const scaleInfo = colorScaleInfo[metric];
    if (scaleInfo.total_values === 0) {
      return 0;
    }

    // Calculate simple average of the metric across all channels in the region
    let totalValue = 0;
    let count = 0;

    Object.entries(regionData).forEach(([, data]) => {
      if (
        data &&
        typeof data === "object" &&
        data[metric] != null &&
        data[metric] > 0
      ) {
        totalValue += data[metric];
        count++;
      }
    });

    if (count === 0) {
      return 0;
    }

    const avgValue = totalValue / count;

    // Map the average value to a 0-1 scale using quartiles for better distribution
    const { quartiles } = scaleInfo;

    if (avgValue <= quartiles[0]) return 0;
    if (avgValue >= quartiles[4]) return 1;

    // Find which quartile range the value falls into and interpolate
    for (let i = 0; i < quartiles.length - 1; i++) {
      if (avgValue >= quartiles[i] && avgValue <= quartiles[i + 1]) {
        const rangeSize = quartiles[i + 1] - quartiles[i];
        const valueInRange = avgValue - quartiles[i];
        const quartilePosition = rangeSize > 0 ? valueInRange / rangeSize : 0;
        return (i + quartilePosition) / (quartiles.length - 1);
      }
    }

    return 0.5; // Fallback to middle value
  };

  // Transform data for map coloring while preserving original for tooltips
  const getMapColoringData = (dataType) => {
    const rawData = allMapData[dataType] || {};
    const coloringData = {};

    Object.entries(rawData).forEach(([region, regionData]) => {
      coloringData[region] = calculateDynamicColorValue(
        regionData,
        selectedColorMetric,
      );
    });

    return coloringData;
  };

  // Store map instance reference for reset functionality
  const mapInstanceRef = useRef(null);

  const handleResetZoom = () => {
    if (mapInstanceRef.current) {
      try {
        // Reset to initial state using the stored map instance
        mapInstanceRef.current.transX = 0;
        mapInstanceRef.current.transY = 0;
        mapInstanceRef.current.scale = 1;
        mapInstanceRef.current.applyTransform();
      } catch (error) {
        console.warn(
          "Reset using stored instance failed, trying alternative approach",
          error,
        );
        // Alternative approach: trigger a re-render by updating the key
        setIsShowMap(false);
        setTimeout(() => setIsShowMap(true), 100);
      }
    } else {
      console.warn("No map instance available for reset, triggering re-render");
      // Fallback: trigger a re-render
      setIsShowMap(false);
      setTimeout(() => setIsShowMap(true), 100);
    }
  };

  const handleToggleColors = () => {
    setIsColorsInverted(!isColorsInverted);
  };

  const handleMetricChange = (event) => {
    if (onColorMetricChange) {
      onColorMetricChange(event.target.value);
    }
  };

  // Format data type for display
  const formatDataType = (type) => {
    const dataTypeNames = {
      sellthrough: "Sell Through",
      sellout: "Sell Out",
      sellin: "Sell In",
    };
    return dataTypeNames[type] || type.replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Generate CSS gradient from color scale
  const getGradientColors = () => {
    const colors = isColorsInverted ? invertedColorScale : colorScale;
    return colors.join(", ");
  };

  useEffect(() => {
    const loadMapData = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsShowMap(false); // Hide map while loading

        if (selectedScenario) {
          // Create cache key from all relevant parameters
          const cacheKey = JSON.stringify({
            selectedScenario,
            appliedFilters,
            selectedTooltipDetail,
            selectedLocation,
            selectedVolume,
          });

          // Check cache first
          if (cachedMapData.current[cacheKey]) {
            console.log(
              "✅ Using cached map data for scenario:",
              selectedScenario,
            );
            const cached = cachedMapData.current[cacheKey];

            setAllMapData(cached.map_data);
            setColorScaleInfo(cached.color_scale_info || {});
            setMapToDisplay(mapConfigs.br);

            setLoading(false);
            setTimeout(() => setIsShowMap(true), 100);
            return;
          }

          try {
            // Use unified API - automatically handles both filtered and unfiltered requests
            const apiData = await getAllMapData(
              selectedScenario,
              appliedFilters,
              selectedTooltipDetail,
              selectedLocation,
              selectedVolume,
            );

            console.log(
              "Map data loaded successfully for scenario:",
              selectedScenario,
              appliedFilters ? "with filters:" : "(no filters)",
              appliedFilters || "",
              "Color scale info:",
              apiData.color_scale_info,
            );

            // Cache the result
            cachedMapData.current[cacheKey] = apiData;

            // Set all map data from API response
            setAllMapData(apiData.map_data);

            // Set dynamic color scale information
            setColorScaleInfo(apiData.color_scale_info || {});

            // Set map configuration - using only Brazil for now
            // const countryCode = apiData.map_to_display || "us"; // Commented out - using only Brazil for now
            setMapToDisplay(mapConfigs.br); // Always use Brazil for now

            // Show map only after successful data loading
            setTimeout(() => setIsShowMap(true), 100);
          } catch (apiError) {
            console.error("Failed to load map data:", apiError);
            setError("Failed to load map data");
            setAllMapData({});
            setMapToDisplay(mapConfigs.br); // Changed to Brazil - using only Brazil for now
            // Don't show map on error
          }
        } else {
          // When no scenario is selected, still set a default map configuration
          setAllMapData({});
          // Use Brazil as the default map configuration
          setMapToDisplay(mapConfigs.br);
          setIsShowMap(false);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error loading map data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, [
    selectedScenario,
    appliedFilters,
    selectedTooltipDetail,
    selectedLocation,
    selectedVolume,
  ]);

  // Generate varied gray values for regions when no scenario is selected
  const generateNoScenarioData = (mapConfig) => {
    if (!mapConfig || !mapConfig.map) return {};

    const regions = Object.keys(mapConfig.map.paths || {});
    const grayValues = {};

    // Create varied gray values between 0.2 and 0.8 for visual variety
    regions.forEach((region) => {
      // Use a deterministic but varied approach based on region name hash
      const hash = region.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);
      // Normalize to 0.2-0.8 range for subtle gray variation
      grayValues[region] = 0.2 + (Math.abs(hash) % 60) / 100;
    });

    return grayValues;
  };

  // Format tooltip with StepBox popover styling
  const formatTooltip = (regionName, value) => {
    if (value === undefined || value === null) {
      return `
        <div style="
          font-family: 'Roboto', Arial, sans-serif;
          min-width: 50px;
          max-width: 300px;
          padding: 12px;
          background: #555555;
          color: white;
          border-radius: 6px;
          font-size: 11px;
          line-height: 1.4;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          text-align: center;
        ">
          <div style="
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 6px;
          ">
            ${regionName}
          </div>
          <div style="
            font-size: 11px;
            color: rgba(255, 255, 255, 0.8);
            font-style: italic;
          ">
            ${t("tooltip.no_data")}
          </div>
        </div>
      `;
    }

    // Handle new nested data structure with rich tooltip data (styled like StepBox popover)
    if (typeof value === "object" && !Array.isArray(value)) {
      // Create HTML table styled exactly like StepBox popover with rich format
      let tableHTML = `
        <div style="
          font-family: 'Roboto', Arial, sans-serif;
          min-width: 320px;
          max-width: 420px;
          padding: 12px;
          background: #555555;
          color: white;
          border-radius: 6px;
          font-size: 11px;
          line-height: 1.4;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        ">
          <div style="
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            padding-bottom: 6px;
          ">
            ${regionName}
          </div>
          <table style="
            width: 100%;
            border-collapse: collapse;
          ">
            <thead>
              <tr>
                <th style="
                  padding: 4px 4px;
                  font-size: 9px;
                  font-weight: 500;
                  color: rgba(255, 255, 255, 0.7);
                  text-align: left;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                ">${
                  selectedTooltipDetail === "channel"
                    ? "Channel"
                    : selectedTooltipDetail === "industry"
                      ? "Industry"
                      : selectedTooltipDetail === "client"
                        ? "Client"
                        : "Point of Sale"
                }</th>
                <th style="
                  padding: 4px 4px;
                  font-size: 9px;
                  font-weight: 500;
                  color: rgba(255, 255, 255, 0.7);
                  text-align: center;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                ">Margin</th>
                <th style="
                  padding: 4px 4px;
                  font-size: 9px;
                  font-weight: 500;
                  color: rgba(255, 255, 255, 0.7);
                  text-align: center;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                ">Price Net</th>
                <th style="
                  padding: 4px 4px;
                  font-size: 9px;
                  font-weight: 500;
                  color: rgba(255, 255, 255, 0.7);
                  text-align: center;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                ">Taxes</th>
                <th style="
                  padding: 4px 4px;
                  font-size: 9px;
                  font-weight: 500;
                  color: rgba(255, 255, 255, 0.7);
                  text-align: center;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                ">Price Gross</th>
              </tr>
            </thead>
            <tbody>`;

      // Sort channels alphabetically for consistent ordering across all states
      const sortedChannels = Object.keys(value).sort();

      // Add rows in alphabetical order
      sortedChannels.forEach((channel) => {
        const data = value[channel];
        let priceNetValue = "-";
        let marginValue = "-";
        let taxesValue = "-";
        let priceGrossValue = "-";

        // Handle rich tooltip data structure (clean format)
        if (data && typeof data === "object") {
          // Handle price net: show "-" only if null/undefined
          if (data.price_net != null) {
            priceNetValue = `$${data.price_net.toFixed(2)}`;
          }

          // Handle margin percentage: show "-" only if null/undefined
          if (data.margin != null) {
            marginValue = `${data.margin.toFixed(1)}%`;
          }

          // Handle taxes percentage - show 0.0% when taxes is 0, reserve "-" for null/undefined
          taxesValue = data.taxes != null ? `${data.taxes.toFixed(1)}%` : "-";

          // Handle price gross: show "-" only if null/undefined
          if (data.price_gross != null) {
            priceGrossValue = `$${data.price_gross.toFixed(2)}`;
          }
        }

        tableHTML += `
          <tr>
            <td style="
              padding: 3px 4px;
              font-size: 10px;
              color: rgba(255, 255, 255, 0.9);
              font-weight: 500;
              text-align: left;
              max-width: 80px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            ">${channel}</td>
            <td style="
              padding: 3px 4px;
              font-size: 10px;
              color: white;
              font-weight: bold;
              text-align: center;
            ">${marginValue}</td>
            <td style="
              padding: 3px 4px;
              font-size: 10px;
              color: white;
              font-weight: bold;
              text-align: center;
            ">${priceNetValue}</td>
            <td style="
              padding: 3px 4px;
              font-size: 10px;
              color: white;
              font-weight: bold;
              text-align: center;
            ">${taxesValue}</td>
            <td style="
              padding: 3px 4px;
              font-size: 10px;
              color: white;
              font-weight: bold;
              text-align: center;
            ">${priceGrossValue}</td>
          </tr>`;
      });

      tableHTML += `
            </tbody>
          </table>
        </div>`;

      return tableHTML;
    }

    // Fallback for old data format (numeric values)
    const dataTypeLabels = {
      sellthrough: "Sell Through",
      sellout: "Sell Out",
      sellin: "Sell In",
    };

    return `${regionName} | ${dataTypeLabels[dataType]}: ${(100 * value).toFixed(1)}%`;
  };

  if (loading && selectedScenario) {
    // Generate varied gray values for loading state
    const loadingGrayData = generateNoScenarioData(mapToDisplay);

    return (
      <div className={classes.mapContainer} style={{ height: height }}>
        {/* Loading state - Map with ThreeDots overlay */}
        <div className={classes.loadingContainer}>
          {/* Light gray map during loading */}
          <VectorMap
            key={`loading-${selectedScenario}-${mapToDisplay.map ? mapToDisplay.map.name || "map" : "default"}`}
            map={mapToDisplay.map}
            backgroundColor="transparent"
            zoomOnScroll={false}
            zoomButtons={false}
            showTooltip={false}
            containerStyle={{
              width: "100%",
              height: "100%",
              opacity: 0.4, // More transparent during loading
              pointerEvents: "none", // Disable all pointer events
            }}
            containerClassName="interactive-map loading-map"
            regionStyle={{
              initial: {
                fill: "#f0f0f0", // Lighter gray during loading
                "fill-opacity": 0.6,
                stroke: "#e0e0e0",
                "stroke-width": mapToDisplay.strokeWidth,
                "stroke-opacity": 1,
              },
              hover: {
                fill: "#f0f0f0", // Prevent hover changes during loading
                "fill-opacity": 0.6,
                cursor: "default",
              },
            }}
            series={{
              regions: [
                {
                  values: loadingGrayData,
                  scale: [
                    "#f8f8f8", // Even lighter grays for loading state
                    "#f0f0f0",
                    "#e8e8e8",
                    "#e0e0e0",
                    "#d8d8d8",
                  ],
                  normalizeFunction: "linear",
                },
              ],
            }}
            onRegionTipShow={(e) => {
              // Completely disable tooltip by preventing default behavior
              e.preventDefault();
              return false;
            }}
            regionLabelStyle={{
              initial: {
                display: "none",
              },
            }}
          />

          {/* ThreeDots loading indicator overlaid on the map */}
          <div className={classes.loadingOverlay}>
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color={clientColor}
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </div>
        </div>
      </div>
    );
  }

  // Handle loading state when no scenario is selected (original behavior)
  if (loading && !selectedScenario) {
    return (
      <div className={classes.mapContainer} style={{ height: height }}>
        <div className={classes.errorContainer}>
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color={clientColor}
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.mapContainer} style={{ height: height }}>
        <div className={classes.errorContainer}>Error loading map: {error}</div>
      </div>
    );
  }

  if (!selectedScenario) {
    const noScenarioGrayData = generateNoScenarioData(mapToDisplay);

    return (
      <div className={classes.mapContainer} style={{ height: height }}>
        {/* No Scenario Map - with varied gray states */}
        <div className={classes.mapContent}>
          <VectorMap
            key={`no-scenario-${mapToDisplay.map ? mapToDisplay.map.name || "map" : "default"}`}
            map={mapToDisplay.map}
            backgroundColor="transparent"
            zoomOnScroll={false}
            zoomButtons={false}
            showTooltip={false}
            containerStyle={{
              width: "100%",
              height: "100%",
              opacity: 0.8,
              pointerEvents: "none", // Disable all pointer events
            }}
            containerClassName="interactive-map no-scenario-map"
            regionStyle={{
              initial: {
                fill: "#e8e8e8",
                "fill-opacity": 0.7,
                stroke: "#d0d0d0",
                "stroke-width": mapToDisplay.strokeWidth,
                "stroke-opacity": 1,
              },
              hover: {
                fill: "#c8c8c8",
                "fill-opacity": 0.8,
                cursor: "default",
              },
            }}
            series={{
              regions: [
                {
                  values: noScenarioGrayData,
                  scale: [
                    "#f5f5f5",
                    "#e0e0e0",
                    "#d0d0d0",
                    "#c0c0c0",
                    "#b0b0b0",
                  ],
                  normalizeFunction: "linear",
                },
              ],
            }}
            onRegionTipShow={(e) => {
              // Completely disable tooltip by preventing default behavior
              e.preventDefault();
              return false;
            }}
            regionLabelStyle={{
              initial: {
                display: "none",
              },
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.mapContainer} style={{ height: height }}>
      {/* Map Content */}
      {isShowMap && (
        <div ref={mapRef} className={classes.mapContent}>
          {/* Reset zoom button - positioned below zoom out button */}
          <Tooltip title="Reset Zoom">
            <button className={classes.resetButton} onClick={handleResetZoom}>
              ↻
            </button>
          </Tooltip>

          <VectorMap
            ref={vectorMapRef}
            key={`loaded-${dataType}-${Object.keys(allMapData[dataType] || {}).length}-${selectedScenario}-${selectedColorMetric}-${selectedTooltipDetail}-${isColorsInverted}`}
            map={mapToDisplay.map}
            backgroundColor="transparent"
            zoomOnScroll={true}
            zoomButtons={true}
            zoomMin={1}
            zoomMax={10}
            scale={1}
            zoomStep={1.4}
            containerClassName={`interactive-map ${classes.vectorMapContainer}`}
            regionStyle={{
              initial: {
                fill: "#e4e4e4",
                "fill-opacity": 0.8,
                stroke: "white",
                "stroke-width": mapToDisplay.strokeWidth,
                "stroke-opacity": 1,
              },
              hover: {
                fill: "#c4c4c4",
                "fill-opacity": 1,
                cursor: "default",
              },
            }}
            series={{
              regions: [
                {
                  values: getMapColoringData(dataType),
                  scale: isColorsInverted ? invertedColorScale : colorScale,
                },
              ],
            }}
            onRegionTipShow={(e, el, code) => {
              const regionName = el.html();
              const value = (allMapData[dataType] || {})[code];
              el.html(formatTooltip(regionName, value));
            }}
            onLoaded={(mapObject) => {
              // Store the map instance for reset functionality
              mapInstanceRef.current = mapObject;
            }}
          />

          {/* Legend Component */}
          <div className={classes.legendContainer}>
            {/* Toggle colors button - positioned above legend */}
            <Tooltip title="Toggle Colors">
              <button
                className={classes.toggleColorsButton}
                onClick={handleToggleColors}
              >
                <InvertColorsIcon />
              </button>
            </Tooltip>
            <div
              className={classes.legendGradient}
              style={{
                background: `linear-gradient(to top, ${getGradientColors()})`,
              }}
            />
            <div className={classes.legendText}>
              <div className={classes.legendHigh}>High</div>
              <div className={classes.legendCenter}>
                <Select
                  value={selectedColorMetric}
                  onChange={handleMetricChange}
                  className={classes.legendDropdown}
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem
                    value="price_net"
                    className={classes.selectMenuItem}
                  >
                    Price Net
                  </MenuItem>
                  <MenuItem value="margin" className={classes.selectMenuItem}>
                    Margin
                  </MenuItem>
                  <MenuItem value="taxes" className={classes.selectMenuItem}>
                    Taxes
                  </MenuItem>
                  <MenuItem
                    value="price_gross"
                    className={classes.selectMenuItem}
                  >
                    Price Gross
                  </MenuItem>
                </Select>
                <div className={classes.legendDataType}>
                  ({formatDataType(dataType)})
                </div>
              </div>
              <div className={classes.legendLow}>Low</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

InteractiveMap.propTypes = {
  selectedScenario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appliedFilters: PropTypes.shape({
    product: PropTypes.string,
    brand: PropTypes.string,
  }),
  height: PropTypes.string,
  dataType: PropTypes.string,
  selectedColorMetric: PropTypes.string,
  selectedTooltipDetail: PropTypes.string,
  selectedLocation: PropTypes.string,
  selectedVolume: PropTypes.string,
  onDataTypeChange: PropTypes.func,
  onColorMetricChange: PropTypes.func,
  showControls: PropTypes.bool,
};

export default InteractiveMap;
